import { useState } from "react";
import { Link, useLocation } from "wouter";
import { Mic, History, CreditCard, User, Rss, X, AlertTriangle, Lock, HelpCircle } from "lucide-react";
import { useUser } from "@clerk/react";
import { usePlan } from "../../hooks/use-plan";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

const BANNER_KEY = "payment-banner-dismissed";
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000;

function isBannerDismissed(cycleId: string | null | undefined): boolean {
  try {
    const raw = localStorage.getItem(BANNER_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { dismissedAt?: number; cycleId?: string | null };
    if (!parsed.dismissedAt) return false;
    if (Date.now() - parsed.dismissedAt >= DISMISS_DURATION_MS) return false;
    if (cycleId != null && parsed.cycleId !== cycleId) return false;
    return true;
  } catch {
    return false;
  }
}

export function Layout({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const { user } = useUser();
  const { data: plan } = usePlan();

  const [dismissedCycleId, setDismissedCycleId] = useState<string | null | undefined>(undefined);

  function dismissBanner() {
    const cycleId = plan?.nextBillingDate ?? null;
    try {
      localStorage.setItem(BANNER_KEY, JSON.stringify({ dismissedAt: Date.now(), cycleId }));
    } catch {
    }
    setDismissedCycleId(cycleId);
  }

  const currentCycleId = plan?.nextBillingDate ?? null;
  const sessionDismissed = dismissedCycleId !== undefined && dismissedCycleId === currentCycleId;
  const isStoredDismissal = isBannerDismissed(plan?.nextBillingDate);
  const showPaymentFailedBanner =
    plan?.plan === "pro" &&
    plan?.subscriptionStatus === "past_due" &&
    !sessionDismissed &&
    !isStoredDismissal;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {showPaymentFailedBanner && (
        <div className="bg-destructive/10 border-b border-destructive/30 text-destructive px-4 py-3">
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm font-medium">
              <AlertTriangle size={16} className="shrink-0" />
              <span>
                Your last payment failed. Please{" "}
                <Link href="/billing" className="underline underline-offset-2 hover:opacity-80">
                  update your payment details
                </Link>{" "}
                to keep your Pro subscription active.
              </span>
            </div>
            <button
              onClick={dismissBanner}
              aria-label="Dismiss payment warning"
              className="shrink-0 rounded-sm p-1 hover:bg-destructive/20 transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}
      <header className="border-b border-border bg-card sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2 group">
            <img
              src={`${basePath}/pa-icon.jpg`}
              alt="Podcasts Abbreviated"
              className="h-8 w-8 rounded-sm object-cover group-hover:scale-105 transition-transform"
            />
            <span className="font-mono font-bold tracking-tight uppercase text-foreground">
              Podcasts <span className="text-primary">Abbreviated</span>
            </span>
          </Link>

          <nav className="flex items-center gap-1">
            <Link
              href="/upload"
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-sm transition-colors ${location === "/upload" ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"}`}
            >
              <Mic size={16} />
              New Job
            </Link>
            <Link
              href="/jobs"
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-sm transition-colors ${location.startsWith("/jobs") ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"}`}
            >
              <History size={16} />
              History
            </Link>
            <Link
              href="/subscriptions"
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-sm transition-colors ${location === "/subscriptions" ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"}`}
            >
              <Rss size={16} />
              Feeds
              {plan?.plan !== "pro" && (
                <Lock size={11} className="text-muted-foreground/50" />
              )}
            </Link>
            <Link
              href="/help"
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-sm transition-colors ${location === "/help" ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"}`}
            >
              <HelpCircle size={16} />
              Help
            </Link>
            <Link
              href="/billing"
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-sm transition-colors ${location === "/billing" ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"}`}
            >
              {plan?.plan === "pro" ? (
                <span className="inline-flex items-center gap-1.5 text-indigo-400">
                  <CreditCard size={16} />
                  Pro
                </span>
              ) : (
                <>
                  <User size={16} />
                  {user?.firstName ?? "Account"}
                </>
              )}
            </Link>
          </nav>
        </div>
      </header>

      <main className="flex-1 max-w-6xl w-full mx-auto p-4 sm:p-6 lg:p-8">{children}</main>
    </div>
  );
}
